import React, { useRef, useState } from 'react';
import {
  Box,
  ClickAwayListener,
  Button,
  MenuList,
  MenuItem,
  Popper,
  Paper,
  Typography,
  Collapse,
  Stack,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import { componentColors } from '../utils/color';

export interface PartnerMenuProps {
  partnerContextName: string;
  partnerWorkstreams: any;
  partnerWorkstreamsLength: number | null | undefined;
  handlePartnerClick: (id: string) => void;
  handleClose: () => void;
  handleToggle: () => void;
  handleEventChange: () => void;
  open: boolean;
  changeNavigation: (path: string) => void;
}

export const PartnerMenu = ({
  partnerContextName,
  partnerWorkstreams,
  partnerWorkstreamsLength,
  handlePartnerClick,
  handleClose,
  handleToggle,
  handleEventChange,
  open,
  changeNavigation,
}: PartnerMenuProps) => {
  const anchorRef = useRef<HTMLButtonElement>(null);

  const handleListKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Tab') {
      event.preventDefault();
      handleEventChange();
    }
  };

  const handleItemClick = (event: React.MouseEvent<HTMLElement>) => {
    handlePartnerClick(event.currentTarget.dataset.partnerid || '');
  };

  const showSwitch = partnerWorkstreamsLength && partnerWorkstreamsLength > 1;

  return (
    <Box>
      {showSwitch ? (
        <>
          <Button
            onClick={handleToggle}
            variant={'contained'}
            ref={anchorRef}
            sx={{
              background: open ? '#6C696A' : '#1D2026',
              py: 0.5,
              px: 1,
              '&:hover': {
                background: '#6C696A',
              },
            }}
            aria-label={`${open ? 'Close' : 'Open'} partner menu`}
            aria-haspopup="true"
            aria-controls={open ? 'menu-list-profile' : undefined}
            data-testid="switchPartnerBtn"
          >
            <Typography variant="subtitle1" component="span" sx={{ color: '#fff' }}>
              {partnerContextName}
            </Typography>
            {open ? (
              <ExpandLessIcon
                sx={{
                  color: '#fff',
                  marginLeft: '1rem',
                  '&:hover': {
                    background: '#6C696A',
                  },
                }}
                fontSize="small"
              />
            ) : (
              <ExpandMoreIcon
                sx={{
                  color: '#fff',
                  marginLeft: '1rem',
                  '&:hover': {
                    background: '#6C696A',
                  },
                }}
                fontSize="small"
              />
            )}
          </Button>
          <Popper
            open={open}
            anchorEl={anchorRef.current}
            disablePortal={true}
            placement="bottom-end"
            style={{ zIndex: 15, minWidth: '200px' }}
          >
            <ClickAwayListener onClickAway={handleClose}>
              <Paper sx={{ marginTop: '0.5rem' }}>
                <MenuList id="menu-list-artner" onKeyDown={handleListKeyDown} style={{ padding: '0' }}>
                  {partnerWorkstreams && (
                    <Box component="li" sx={{ pt: 2, pr: 1, pb: 1, pl: 2 }}>
                      <Typography variant="overline" color="text.secondary" lineHeight={1}>
                        Switch Partner
                      </Typography>
                    </Box>
                  )}
                  {partnerWorkstreams &&
                    partnerWorkstreams.map((item: any) => {
                      return (
                        <MenuItem
                          key={`switch-${item.partner.id}`}
                          sx={{ margin: '0' }}
                          onClick={handleItemClick}
                          data-partnerid={item.partner.id}
                        >
                          {item.partner.name}
                        </MenuItem>
                      );
                    })}
                  {partnerWorkstreamsLength > 3 && (
                    <MenuItem
                      onClick={() => changeNavigation(`/app/switch-partner`)}
                      sx={{ margin: '0', color: 'primary.main', display: 'flex', alignItems: 'center' }}
                    >
                      <Box sx={{ flex: 1, fontWeight: 'bold' }}>View More</Box>
                      <ChevronRightIcon fontSize="medium" />
                    </MenuItem>
                  )}
                </MenuList>
              </Paper>
            </ClickAwayListener>
          </Popper>
        </>
      ) : (
        <Typography variant="subtitle1" component="span" sx={{ color: '#fff', padding: '0 0.75rem' }}>
          {partnerContextName}
        </Typography>
      )}
    </Box>
  );
};

export const PartnerBurgerMenu = ({
  partnerContextName,
  partnerWorkstreams,
  partnerWorkstreamsLength,
  handlePartnerClick,
  changeNavigation,
}: PartnerMenuProps) => {
  const [open, setOpen] = useState(false);
  const showSwitch = partnerWorkstreamsLength && partnerWorkstreamsLength > 1;

  const handleItemClick = (event: React.MouseEvent<HTMLElement>) => {
    handlePartnerClick(event.currentTarget.dataset.partnerid || '');
  };

  return (
    <Box
      sx={{
        backgroundColor: (theme) =>
          theme.palette.mode === 'dark'
            ? theme.palette.action.selected
            : componentColors(theme.palette.primary).background,
      }}
    >
      <MenuItem sx={{ m: 0 }} disableGutters onClick={() => setOpen((prev) => !prev)}>
        <Stack direction="row" justifyContent="space-between" width="100%">
          <Typography component="span" color="primary.main" fontWeight="bold">
            {partnerContextName}
          </Typography>
          {open ? (
            <ExpandLessIcon
              sx={{
                letterSpacing: '.5px',
                '&:hover': {
                  background: '#6C696A',
                  color: 'white',
                },
              }}
              fontSize="small"
            />
          ) : (
            <ExpandMoreIcon
              sx={{
                letterSpacing: '.5px',
                '&:hover': {
                  background: '#6C696A',
                  color: 'white',
                },
              }}
              fontSize="small"
            />
          )}
        </Stack>
      </MenuItem>
      {showSwitch && (
        <Collapse in={open}>
          {partnerWorkstreams &&
            partnerWorkstreams.map((item: any) => {
              return (
                <MenuItem
                  key={`switch-${item.partner.id}`}
                  sx={{ m: '0', pl: 4 }}
                  onClick={handleItemClick}
                  data-partnerid={item.partner.id}
                >
                  {item.partner.name}
                </MenuItem>
              );
            })}
          {partnerWorkstreamsLength > 3 && (
            <MenuItem
              onClick={() => changeNavigation(`/app/switch-partner`)}
              sx={{ m: 0, pl: 4, color: 'primary.main', display: 'flex', alignItems: 'center' }}
            >
              <Box sx={{ flex: 1, fontWeight: 'bold' }}>View More</Box>
              <ChevronRightIcon fontSize="small" />
            </MenuItem>
          )}
        </Collapse>
      )}
    </Box>
  );
};
